import { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '../components/Layout'
import CopyButton from '../components/CopyButton'
import { addToHistory } from '../utils/history'

type Mode = 'encode' | 'decode'
type InputType = 'text' | 'image'

function Base64() {
  const { t } = useTranslation()
  const [mode, setMode] = useState<Mode>('encode')
  const [inputType, setInputType] = useState<InputType>('text')
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState('')
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const saveToHistory = useCallback((inputVal: string, outputVal: string) => {
    if (inputVal && outputVal && !outputVal.startsWith('[')) {
      addToHistory({
        tool: 'base64',
        toolName: 'Base64',
        input: inputVal.substring(0, 500),
        output: outputVal.substring(0, 500),
      })
    }
  }, [])

  const handleTextEncode = (text: string) => {
    setInput(text)
    setError('')
    if (!text) {
      setOutput('')
      return
    }
    try {
      const encoded = btoa(unescape(encodeURIComponent(text)))
      setOutput(encoded)
      saveToHistory(text, encoded)
    } catch (err) {
      setError(t('toolPages.base64.encodeError'))
      setOutput('')
    }
  }

  const handleTextDecode = (base64: string) => {
    setInput(base64)
    setError('')
    setImagePreview(null)
    
    if (!base64) {
      setOutput('')
      return
    }
    
    try {
      // Check if it's an image data URL
      if (base64.startsWith('data:image')) {
        setImagePreview(base64)
        setOutput(t('toolPages.base64.imageData'))
        return
      }
      
      // Try to decode as regular base64
      const decoded = decodeURIComponent(escape(atob(base64.trim())))
      setOutput(decoded)
      saveToHistory(base64, decoded)
      
      // Check if decoded content might be an image
      if (base64.length > 100) {
        try {
          const testImage = `data:image/png;base64,${base64.trim()}`
          const img = new Image()
          img.onload = () => setImagePreview(testImage)
          img.src = testImage
        } catch {
          // Not an image, ignore
        }
      }
    } catch (err) {
      setError(t('toolPages.base64.decodeError'))
      setOutput('')
    }
  }

  const handleInputChange = (value: string) => {
    if (mode === 'encode') {
      handleTextEncode(value)
    } else {
      handleTextDecode(value)
    }
  }

  const handleModeChange = (newMode: Mode) => {
    setMode(newMode)
    setInput('')
    setOutput('')
    setError('')
    setImagePreview(null)
    setInputType('text')
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setError('')
    
    if (!file.type.startsWith('image/')) {
      setError(t('toolPages.base64.imageError'))
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      setImagePreview(result)
      setOutput(result)
      setInput(`[${file.name}]`)
    }
    reader.onerror = () => {
      setError(t('toolPages.base64.fileReadError'))
    }
    reader.readAsDataURL(file)
  }

  const handleSwap = () => {
    if (output) {
      const newInput = output
      if (mode === 'encode') {
        setMode('decode')
        handleTextDecode(newInput)
      } else {
        setMode('encode')
        handleTextEncode(newInput)
      }
    }
  }

  const handleClear = () => {
    setInput('')
    setOutput('')
    setError('')
    setImagePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <Layout showBackButton title={t('toolPages.base64.title')}>
      <div className="tool-container">
        <p className="tool-description">
          {t('toolPages.base64.description')}
        </p>

      {/* Mode Toggle */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
          <button
            onClick={() => handleModeChange('encode')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              mode === 'encode'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {t('common.encode')}
          </button>
          <button
            onClick={() => handleModeChange('decode')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              mode === 'decode'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {t('common.decode')}
          </button>
        </div>

        {mode === 'encode' && (
          <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
            <button
              onClick={() => setInputType('text')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                inputType === 'text'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {t('toolPages.base64.text')}
            </button>
            <button
              onClick={() => setInputType('image')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                inputType === 'image'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {t('toolPages.base64.image')}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="card">
          <div className="flex justify-between items-center mb-3">
            <h2 className="section-title">
              {mode === 'encode' ? t('common.input') : t('common.input') + ' (Base64)'}
            </h2>
            <button onClick={handleClear} className="btn-secondary text-sm">
              {t('common.clear')}
            </button>
          </div>

          {mode === 'encode' && inputType === 'image' ? (
            <div className="space-y-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="input"
              />
              {imagePreview && (
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-w-full max-h-64 mx-auto rounded"
                  />
                </div>
              )}
            </div>
          ) : (
            <textarea
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={mode === 'encode' ? t('toolPages.base64.inputPlaceholder') : t('toolPages.base64.decodePlaceholder')}
              className="textarea h-48 scrollbar-thin"
            />
          )}

          {error && <p className="error-message">{error}</p>}
        </div>

        {/* Output Section */}
        <div className="card">
          <div className="flex justify-between items-center mb-3">
            <h2 className="section-title">
              {mode === 'encode' ? t('common.output') + ' (Base64)' : t('common.output')}
            </h2>
            <div className="flex gap-2">
              <button onClick={handleSwap} className="btn-secondary text-sm" disabled={!output}>
                ⇄ {t('common.swap')}
              </button>
              <CopyButton text={output} />
            </div>
          </div>

          <textarea
            value={output}
            readOnly
            placeholder={t('common.output') + '...'}
            className="textarea h-48 bg-gray-50 dark:bg-gray-900 scrollbar-thin"
          />

          {/* Image preview for decode mode */}
          {mode === 'decode' && imagePreview && (
            <div className="mt-4 border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t('toolPages.base64.image')}:</p>
              <img
                src={imagePreview}
                alt="Decoded"
                className="max-w-full max-h-64 mx-auto rounded"
              />
            </div>
          )}
        </div>
      </div>
      </div>
    </Layout>
  )
}

export default Base64
